import React, { useEffect, useRef, useState } from "react";
import { Sparkles, ArrowLeft, ArrowRight, Shuffle, Wind, Eye, Play, Pause } from "lucide-react";
import { PageHero, SectionTitle } from "@/components/Bits";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { AFFIRMATIONS } from "@/lib/content";
import { IMG } from "@/lib/images";

const PHASES = [
  { label: "Breathe In", secs: 4, scale: 1.6 },
  { label: "Hold", secs: 4, scale: 1.6 },
  { label: "Breathe Out", secs: 4, scale: 1 },
  { label: "Hold", secs: 4, scale: 1 },
];

const SENSES = [
  { n: 5, sense: "things you can SEE", hint: "Look around slowly. Name five." },
  { n: 4, sense: "things you can FEEL", hint: "The chair, your feet, the air." },
  { n: 3, sense: "things you can HEAR", hint: "Near sounds and far sounds." },
  { n: 2, sense: "things you can SMELL", hint: "Or two scents you love." },
  { n: 1, sense: "thing you can TASTE", hint: "Or one slow, grateful breath." },
];

function BreathingPacer() {
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState(0);
  const [count, setCount] = useState(4);
  const ref = useRef(null);

  useEffect(() => {
    if (!running) return;
    setCount(PHASES[phase].secs);
    const iv = setInterval(() => {
      setCount((c) => {
        if (c <= 1) { setPhase((p) => (p + 1) % PHASES.length); return PHASES[(phase + 1) % PHASES.length].secs; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [running, phase]);

  const active = PHASES[phase];
  return (
    <div className="bg-bsn-midnight rounded-2xl p-8 text-center border border-bsn-gold/25 min-h-[27rem] flex flex-col items-center justify-center" data-testid="breathing-pacer">
      <div className="h-56 flex items-center justify-center">
        <div
          ref={ref}
          className="w-28 h-28 rounded-full flex items-center justify-center"
          style={{
            transform: `scale(${running ? active.scale : 1})`,
            transition: `transform ${running ? active.secs : 0.6}s ease-in-out`,
            background: "radial-gradient(circle, rgba(245,215,127,0.9), rgba(212,175,55,0.3))",
            boxShadow: "0 0 60px rgba(212,175,55,0.5)",
          }}
        >
          <span className="font-serif text-3xl font-bold text-bsn-midnight">{running ? count : ""}</span>
        </div>
      </div>
      <p className="font-serif text-2xl text-bsn-champagne mt-4 min-h-[2rem]" data-testid="breathing-phase">
        {running ? active.label : "4 · 4 · 4 · 4 Box Breathing"}
      </p>
      <Button onClick={() => setRunning((v) => !v)} className="mt-5 btn-gold font-semibold" data-testid="breathing-toggle">
        {running ? <><Pause className="w-4 h-4 mr-2" /> Pause</> : <><Play className="w-4 h-4 mr-2" /> Begin</>}
      </Button>
    </div>
  );
}

function Grounding() {
  const [step, setStep] = useState(0);
  const cur = SENSES[step];
  return (
    <div className="bg-white rounded-2xl p-8 card-tactile text-center min-h-[27rem] flex flex-col items-center justify-center" data-testid="grounding-widget">
      <Eye className="w-8 h-8 text-bsn-gold mx-auto mb-4" />
      <p className="eyebrow mb-3">5 · 4 · 3 · 2 · 1 Grounding</p>
      <p className="font-serif text-4xl font-bold gold-text">{cur.n}</p>
      <p className="font-serif text-xl text-bsn-midnight mt-1">{cur.sense}</p>
      <p className="text-sm text-bsn-sapphire/70 mt-3">{cur.hint}</p>
      <div className="flex items-center justify-center gap-2 mt-6">
        {SENSES.map((_, i) => (
          <span key={i} className={`w-2 h-2 rounded-full transition-colors ${i === step ? "bg-bsn-gold" : "bg-bsn-sapphire/25"}`} />
        ))}
      </div>
      <Button onClick={() => setStep((s) => (s + 1) % SENSES.length)} className="mt-6 bg-bsn-midnight text-bsn-parchment hover:bg-bsn-navy font-semibold" data-testid="grounding-next">
        {step === SENSES.length - 1 ? "Start Again" : "Next"} <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
}

export default function Affirmations() {
  const [i, setI] = useState(0);
  const [flip, setFlip] = useState(false);

  const next = () => { setFlip(true); setTimeout(() => { setI((v) => (v + 1) % AFFIRMATIONS.length); setFlip(false); }, 180); };
  const prev = () => { setFlip(true); setTimeout(() => { setI((v) => (v - 1 + AFFIRMATIONS.length) % AFFIRMATIONS.length); setFlip(false); }, 180); };
  const shuffle = () => { setFlip(true); setTimeout(() => { setI(Math.floor(Math.random() * AFFIRMATIONS.length)); setFlip(false); }, 180); };

  return (
    <div data-testid="affirmations-page">
      <PageHero
        eyebrow="Rebuilding & Adult Affirmations"
        title="Gentle anchors for the road back to yourself"
        subtitle="Grounding words and calming tools to steady your breath, soften the storm, and remind you that healing is not linear."
        image={IMG.lakeDock}
        testid="affirmations-hero"
      />

      <section className="max-w-4xl mx-auto px-4 py-16 sm:py-20">
        <SectionTitle eyebrow="Daily Anchor Deck" title="Draw a card, take a breath" />
        <Reveal>
          <div className="relative bg-gradient-to-br from-bsn-navy to-bsn-midnight rounded-3xl p-10 sm:p-14 text-center border border-bsn-gold/30 shadow-[0_24px_60px_rgba(11,25,44,0.25)]">
            <Sparkles className="w-8 h-8 text-bsn-gold mx-auto mb-6" />
            <p
              className="font-serif text-2xl sm:text-3xl lg:text-4xl font-medium text-bsn-champagne leading-snug min-h-[4rem] flex items-center justify-center text-balance transition-all duration-200"
              style={{ opacity: flip ? 0 : 1, transform: flip ? "translateY(10px)" : "none" }}
              data-testid="affirmation-card"
            >
              “{AFFIRMATIONS[i]}”
            </p>
            <div className="flex items-center justify-center gap-3 mt-10">
              <button onClick={prev} className="p-3 rounded-full border border-bsn-gold/40 text-bsn-champagne hover:bg-bsn-gold/10 transition-colors" data-testid="affirmation-card-prev" aria-label="Previous">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <button onClick={shuffle} className="px-5 py-3 rounded-full btn-gold font-semibold inline-flex items-center gap-2" data-testid="affirmation-shuffle">
                <Shuffle className="w-4 h-4" /> Shuffle
              </button>
              <button onClick={next} className="p-3 rounded-full border border-bsn-gold/40 text-bsn-champagne hover:bg-bsn-gold/10 transition-colors" data-testid="affirmation-card-next" aria-label="Next">
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="max-w-5xl mx-auto px-4 pb-20 grid md:grid-cols-2 gap-6">
        <Reveal><div><div className="flex items-center gap-2 mb-4"><Wind className="w-5 h-5 text-bsn-bronze" /><h3 className="font-serif text-xl font-semibold text-bsn-midnight">Box Breathing Pacer</h3></div><BreathingPacer /></div></Reveal>
        <Reveal delay={100}><div><div className="flex items-center gap-2 mb-4"><Eye className="w-5 h-5 text-bsn-bronze" /><h3 className="font-serif text-xl font-semibold text-bsn-midnight">Sensory Grounding</h3></div><Grounding /></div></Reveal>
      </section>
    </div>
  );
}
